import { AgendaEvent } from '@/lib/api/common/interfaces/agenda.interface'
import {
    getStatusColor,
    EVENT_STATUS_LABELS,
    getEventTypeLabels,
} from '../../utils/status-colors'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { formatTime } from '../../utils/calendar-utils'
import { MdPerson, MdAccessTime } from 'react-icons/md'

interface EventCardProps {
    event: AgendaEvent
    onClick?: (event: AgendaEvent) => void
    compact?: boolean
    showTime?: boolean
    /** Linha de status e tipo — desligada quando o cartão é baixo demais. */
    showDetails?: boolean
}

export function EventCard({
    event,
    onClick,
    compact = false,
    showTime = true,
    showDetails = true,
}: EventCardProps) {
    const eventTypeLabels = getEventTypeLabels(useServiceLabel())
    const colors = getStatusColor(event.status)
    // O paciente é quem identifica o compromisso na tela; o título ("Consulta
    // #3723") vira informação secundária, porque o número não diz nada a quem
    // está olhando a agenda.
    const primaryLabel = event.client?.name || event.title
    const startTime = formatTime(new Date(event.startDate))
    const endTime = formatTime(new Date(event.endDate))

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        if (onClick) {
            onClick(event)
        }
    }

    if (compact) {
        return (
            <div
                className={`event-card px-2 py-1 rounded text-xs truncate ${colors.bg} ${colors.border} border-l-4 ${colors.text} ${event.isOverbooking ? 'event-card-overbooking' : ''}`}
                onClick={handleClick}
                title={`${primaryLabel} - ${event.title} - ${startTime}`}
            >
                <span className="font-medium">{startTime}</span> {primaryLabel}
            </div>
        )
    }

    return (
        <div
            className={`event-card p-2 rounded-lg ${colors.bg} ${colors.border} border-l-4 ${event.isOverbooking ? 'event-card-overbooking' : ''}`}
            onClick={handleClick}
        >
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <p
                        className={`font-semibold text-sm truncate ${colors.text}`}
                    >
                        {event.client ? (
                            <span className="flex items-center gap-1">
                                <MdPerson className="text-sm opacity-70 flex-shrink-0" />
                                <span className="truncate">
                                    {event.client.name}
                                </span>
                            </span>
                        ) : (
                            event.title
                        )}
                    </p>
                    {event.client && (
                        <p className="text-xs text-base-content/60 truncate mt-0.5">
                            {event.title}
                        </p>
                    )}
                </div>
                {event.isOverbooking && (
                    <span className="badge badge-xs badge-warning">Encaixe</span>
                )}
            </div>

            {showTime && (
                <div className="flex items-center gap-1 mt-2">
                    <MdAccessTime className="text-xs text-base-content/60" />
                    <span className="text-xs text-base-content/70">
                        {startTime} - {endTime}
                    </span>
                </div>
            )}

            {showDetails && (
            <div className="flex items-center gap-2 mt-2">
                <span
                    className={`px-2 py-0.5 rounded-full text-xs ${colors.bg} ${colors.text}`}
                >
                    {EVENT_STATUS_LABELS[event.status] || event.status}
                </span>
                {event.type && (
                    <span className="text-xs text-base-content/50">
                        {eventTypeLabels[event.type] || event.type}
                    </span>
                )}
            </div>
            )}
        </div>
    )
}

interface EventStatusBadgeProps {
    status: string
    size?: 'xs' | 'sm' | 'md'
}

export function EventStatusBadge({ status, size = 'sm' }: EventStatusBadgeProps) {
    const colors = getStatusColor(status)
    const sizeClasses = {
        xs: 'px-1.5 py-0.5 text-xs',
        sm: 'px-2 py-1 text-xs',
        md: 'px-3 py-1.5 text-sm',
    }

    return (
        <span
            className={`inline-flex items-center gap-1 rounded-full font-medium ${colors.bg} ${colors.text} ${sizeClasses[size]}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`}></span>
            {EVENT_STATUS_LABELS[status] || status}
        </span>
    )
}
